import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { assets as assetsApi } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import type { ResolvedLineage } from '../lib/types';

/**
 * Provenance panel — F-052 (FINAL_STATE §4 trust layer; "the moat is only a
 * moat when it is visible"). Renders an asset's resolved `asset_lineage` chain
 * of custody (source → derivations), ordered root-first. Fetches on mount from
 * GET /v1/assets/:id/lineage; the caller (asset owner) is authorized server-
 * side. A non-derived original reports itself as its own root.
 */
export function ProvenancePanel({ assetId }: { assetId: string }) {
  const { t } = useI18n();
  const [lineage, setLineage] = useState<ResolvedLineage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    assetsApi
      .lineage(assetId, controller.signal)
      .then((res) => setLineage(res.lineage))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(apiErrorMessage(err, t));
      });
    return () => controller.abort();
  }, [assetId, t]);

  if (error) {
    return (
      <p className="type-caption text-danger" role="alert">
        {error}
      </p>
    );
  }

  if (!lineage) {
    return <p className="type-caption text-muted">{t.provenance.loading}</p>;
  }

  if (!lineage.is_derived) {
    return <p className="type-caption text-muted">{t.provenance.originalRoot}</p>;
  }

  return (
    <div className="rounded border border-line bg-surface-2 p-3" data-testid="provenance-panel">
      <p className="type-label text-secondary mb-2">
        {t.provenance.title} · {t.provenance.hops(lineage.depth)}
      </p>
      <ol className="flex flex-col gap-1">
        {lineage.chain.map((node, i) => {
          const isTarget = node.asset_id === lineage.target_asset_id;
          return (
            <li key={node.asset_id} className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="type-caption text-muted tnum">{i + 1}.</span>
                <span className={`type-body-sm ${isTarget ? 'text-accent font-bold' : 'text-primary'} truncate`}>
                  {node.title}
                </span>
                {isTarget ? (
                  <span className="type-label text-accent">{t.provenance.thisAsset}</span>
                ) : null}
              </div>
              <div className="type-caption text-muted pl-5">
                {node.kind}
                {node.relation_to_child ? ` · ${node.relation_to_child}` : ''}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
